import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth, extractError } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const HERO_IMG = "https://images.pexels.com/photos/19571010/pexels-photo-19571010.jpeg";

export default function Signup() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ company_name: "", mobile: "", password: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company_name.trim()) {
      toast.error("Business name is required");
      return;
    }
    if (!/^\d{10}$/.test(form.mobile)) {
      toast.error("Mobile must be 10 digits");
      return;
    }
    if (form.password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    setLoading(true);
    try {
      await register({
        name: form.company_name,
        company_name: form.company_name,
        mobile: form.mobile,
        password: form.password,
        email: null,
        address: null,
        company_type_id: null,
        company_type_name: null,
      });
      toast.success("Workspace created");
      navigate("/onboarding");
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-[#F9F8F6]">
      <div className="hidden lg:block relative">
        <img src={HERO_IMG} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-br from-black/60 to-transparent" />
        <div className="absolute inset-0 p-12 flex flex-col justify-between text-white">
          <Link to="/" className="font-serif text-3xl tracking-tight">
            <img 
              src="/planniq_logo_white.png" 
              alt="EventFlow" 
              className="h-10 w-auto"
            />
          </Link>
          <div>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tighter max-w-lg leading-tight">
              Start in few seconds. Bring your team in next.
            </h2>
            <div className="mt-4 text-sm text-zinc-200">Free Signup. No card required.</div>
          </div>
        </div>
      </div>

      <div className="flex flex-col">
        <div className="px-6 sm:px-12 py-6 flex items-center justify-between">
          <Link to="/" className="lg:hidden font-serif text-2xl tracking-tight text-[#1A1A1A]">
            <img 
              src="/planniq_logo_dark.png" 
              alt="EventFlow" 
              className="h-10 w-auto"
            />
          </Link>
 
        </div>

        <div className="flex-1 flex items-center justify-center px-6 sm:px-12 py-8">
          <form onSubmit={submit} className="w-full max-w-md space-y-5" data-testid="signup-form">
            <div>
              <div className="text-xs uppercase tracking-[0.25em] text-[#D85C38]">Create your workspace</div>
              <h1 className="font-serif text-4xl md:text-5xl tracking-tighter mt-2">Get started</h1>
            </div>

            <div>
              <Label htmlFor="company">Business name</Label>
              <Input id="company" required value={form.company_name} onChange={update("company_name")} className="mt-1.5 h-11" placeholder="e.g. Aanya Bridal Studio" data-testid="signup-company-input" />
            </div>

            <div>
              <Label htmlFor="mobile">Mobile</Label>
              <Input id="mobile" required inputMode="numeric" maxLength={10} value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value.replace(/\D/g, "") })} className="mt-1.5 h-11" placeholder="9876543210" data-testid="signup-mobile-input" />
            </div>

            <div>
              <Label htmlFor="password">Create password</Label>
              <Input id="password" required type="password" minLength={8} value={form.password} onChange={update("password")} className="mt-1.5 h-11" data-testid="signup-password-input" />
              <div className="text-xs text-zinc-500 mt-1">Minimum 8 characters.</div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-11 rounded-full bg-[#D85C38] hover:bg-[#B84A2A] text-white"
              data-testid="signup-submit-btn"
            >
              {loading ? "Creating workspace…" : "Get started"}
            </Button>

            <Button
              type="button"
              onClick={() => navigate('/login')}
              className="w-full h-11 rounded-full bg-black text-white hover:bg-black/80"
              data-testid="login-to-signup-link"
            >
              Already have an account? Sign in
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
